import React, { useState } from "react";
import useFetchData from "../../../../hooks/FetchData";
import { http } from "../../../../utils/httpCommon";
import { toast } from "react-hot-toast";
import dayjs from "dayjs";
import { Switch, TextField } from "@mui/material";
import { useAuthUser } from "react-auth-kit";
import ModalComponent from "../../../../components/Modal/Modal2";
import { Button } from "../../../../components/Button/Button";
import LoadingCircle from "../../../../components/LoadingCircle/LoadingCircle";

const CompanyNotes = ({ companyId }) => {
  const auth = useAuthUser();
  const { response: notes, loading, refetch } = useFetchData(
    companyId ? `/company/${companyId}/notes` : null
  );
  const [addOpen, setAddOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [statusLoadingId, setStatusLoadingId] = useState(null);

  const isLandLord = auth()?.role === "Land Lord";
  const canMutate =
    auth()?.role === "Super Admin" ||
    auth()?.role === "Area Manager" ||
    auth()?.role === "Branch Manager" ||
    auth()?.role === "Experience Officer";

  const handleAddNote = async (e) => {
    e.preventDefault();
    const trimmed = newNote.trim();
    if (!trimmed) {
      toast.error("Please enter a note");
      return;
    }
    setSubmitLoading(true);
    try {
      await http.post(`/company/${companyId}/notes`, JSON.stringify({ note: trimmed }));
      toast.success("Note saved");
      setNewNote("");
      setAddOpen(false);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to save note");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleStatusChange = async (noteId, checked) => {
    if (!canMutate || isLandLord) return;
    setStatusLoadingId(noteId);
    try {
      await http.patch(
        `/company/${companyId}/notes/${noteId}/status`,
        JSON.stringify({ isActive: checked })
      );
      toast.success("Status updated");
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to update status");
    } finally {
      setStatusLoadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="mt-6 flex justify-center py-12">
        <LoadingCircle />
      </div>
    );
  }

  const list = Array.isArray(notes) ? notes : [];

  return (
    <div className="mt-6 flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {canMutate && !isLandLord ? (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="bg-[#32B3BE] font-bold text-white text-[16px] px-4 py-2 rounded-md hover:bg-[#32b2bebd] transition-all"
          >
            + Add Note
          </button>
        ) : (
          <span className="text-[#8E8E8E] text-[16px]">Notes</span>
        )}
      </div>

      <ModalComponent opened={addOpen} setOpened={setAddOpen} size="md" onClose={() => setAddOpen(false)}>
        <form onSubmit={handleAddNote} className="flex flex-col gap-4 p-1">
          <div className="text-[22px] font-bold">Add Note</div>
          <p className="text-[#797979] text-[14px]">
            Notes cannot be edited after saving. You can still change status later.
          </p>
          <TextField
            label="Note"
            multiline
            minRows={4}
            maxRows={12}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            fullWidth
            required
          />
          <div className="flex justify-end gap-3">
            <Button type="button" secondary invert onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" secondary loading={submitLoading}>
              Save
            </Button>
          </div>
        </form>
      </ModalComponent>

      <div className="w-full overflow-x-auto border border-[#E4E4E4] rounded-[10px]">
        <table className="w-full min-w-[640px] text-left border-collapse">
          <thead>
            <tr className="bg-[#F8F9FA] text-[#011516]">
              <th className="p-3 font-semibold text-[15px]">Notes</th>
              <th className="p-3 font-semibold text-[15px] w-[180px]">Last saved</th>
              <th className="p-3 font-semibold text-[15px] w-[160px]">Admin</th>
              <th className="p-3 font-semibold text-[15px] w-[100px]">Status</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-6 text-center text-[#8E8E8E]">
                  No notes yet
                </td>
              </tr>
            ) : (
              list.map((row) => (
                <tr key={row._id} className="border-t border-[#E4E4E4]">
                  <td className="p-3 align-top">
                    <div className="border border-[#E4E4E4] rounded-[10px] px-3 py-2 bg-[#FAFAFA] text-[15px] whitespace-pre-wrap break-words">
                      {row.note}
                    </div>
                  </td>
                  <td className="p-3 align-top text-[14px] text-[#797979]">
                    {row.createdAt
                      ? dayjs(row.createdAt).format("HH:mm:ss - DD/MM/YYYY")
                      : "N/A"}
                  </td>
                  <td className="p-3 align-top text-[15px] font-medium">
                    {row.admin?.name || "N/A"}
                  </td>
                  <td className="p-3 align-top">
                    <Switch
                      checked={!!row.isActive}
                      disabled={!canMutate || isLandLord || statusLoadingId === row._id}
                      onChange={(_, checked) => handleStatusChange(row._id, checked)}
                      color="success"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end text-[14px] text-[#797979]">
        Rows per page: 10 - {list.length === 0 ? "0-0 of 0" : `1-${list.length} of ${list.length}`}
      </div>
    </div>
  );
};

export default CompanyNotes;
